import type { ReactNode } from "react";
import { IconTrash, IconPlus } from "../icons";

/**
 * Peças da CENTRAL DO EVENTO usadas por mais de uma aba.
 *
 * Ficam no nível do arquivo (nunca declaradas dentro de outro componente): um
 * componente criado dentro de outro vira função nova a cada tecla, o React
 * remonta a subárvore e o campo perde o foco — o mesmo defeito que travou a
 * página pública de avaliação.
 */

/* ------------------------------------------------------------------ *
 *  Formatação                                                          *
 * ------------------------------------------------------------------ */

/** "2026-09-02" → "02/09/2026" (a data vem como TEXTO, sem fuso). */
export function dataBr(iso?: string | null): string {
  if (!iso) return "";
  const [a, m, d] = String(iso).slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : String(iso);
}

/** "2026-09-02" → "02 SET 2026" (o formato do card). */
const MES_CURTO = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];
export function dataCard(iso?: string | null): string {
  if (!iso) return "Sem data";
  const [a, m, d] = String(iso).slice(0, 10).split("-");
  const mes = MES_CURTO[Number(m) - 1] ?? m;
  return `${d} ${mes} ${a}`;
}

/** O período do evento numa linha: um dia ou vários, com ou sem hora. */
export function periodoTexto(e: {
  data?: string | null;
  data_fim?: string | null;
  hora_inicio?: string | null;
  hora_fim?: string | null;
  dia_inteiro?: boolean;
}): string {
  const ini = dataBr(e.data);
  const fim = e.data_fim && e.data_fim !== e.data ? dataBr(e.data_fim) : "";
  const dias = fim ? `${ini} → ${fim}` : ini || "Sem data";
  if (e.dia_inteiro) return `${dias} · dia inteiro`;
  const horas = [e.hora_inicio, e.hora_fim].filter(Boolean).join(" — ");
  return horas ? `${dias} · ${horas}` : dias;
}

export function moeda(v: unknown): string {
  const n = Number(v) || 0;
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/** Aceita "1.500,00" e "1500.00" — o DP digita dos dois jeitos. */
export function paraNumero(v: unknown): number {
  const s = String(v ?? "").trim();
  if (!s) return 0;
  const limpo = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s;
  const n = Number(limpo);
  return Number.isFinite(n) ? n : 0;
}

/** Cor do selo conforme a situação do evento. */
export function tomDoStatus(status?: string | null): string {
  switch (status) {
    case "Confirmado":
      return "pill-ok";
    case "Em andamento":
      return "pill-warn";
    case "Concluído":
      return "pill-neutral";
    case "Cancelado":
      return "pill-danger";
    default:
      return "pill-neutral"; // Planejamento
  }
}

export function tomDaPrioridade(p?: string | null): string {
  switch (p) {
    case "Urgente":
      return "pill-danger";
    case "Alta":
      return "pill-warn";
    case "Baixa":
      return "pill-neutral";
    default:
      return "pill-ok"; // Média
  }
}

/* ------------------------------------------------------------------ *
 *  Blocos de tela                                                      *
 * ------------------------------------------------------------------ */

/** Cabeçalho de uma seção editável, com o botão de acrescentar item. */
export function SecaoHead({
  titulo,
  contagem,
  acao,
  onAcao,
  children,
}: {
  titulo: string;
  contagem?: string;
  acao?: string;
  onAcao?: () => void;
  children?: ReactNode;
}) {
  return (
    <header className="ev-secao-head">
      <h4>
        {titulo}
        {contagem && <small className="muted"> {contagem}</small>}
      </h4>
      <div className="ev-secao-acoes">
        {children}
        {acao && onAcao && (
          <button type="button" className="btn-secondary btn-sm" onClick={onAcao}>
            <IconPlus size={15} /> {acao}
          </button>
        )}
      </div>
    </header>
  );
}

/** Barra de progresso (checklist, orçamento). */
export function Progresso({ pct, tom = "brand" }: { pct: number; tom?: "brand" | "warn" }) {
  return (
    <div className="ev-barra">
      <div
        className={`ev-barra-fill ${tom === "warn" ? "ev-barra-warn" : ""}`}
        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
      />
    </div>
  );
}

/** Botão de remover linha — o mesmo em todas as listas. */
export function BotaoRemover({ titulo, onClick }: { titulo: string; onClick: () => void }) {
  return (
    <button type="button" className="icon-btn icon-btn-danger" title={titulo} onClick={onClick}>
      <IconTrash size={15} />
    </button>
  );
}

/** Estado vazio de uma aba (com o convite para a primeira ação). */
export function Vazio({ texto, dica }: { texto: string; dica?: string }) {
  return (
    <div className="empty ev-vazio">
      <strong>{texto}</strong>
      {dica && <small className="muted">{dica}</small>}
    </div>
  );
}
